/**
 * DuplicateEmptyState Component
 *
 * Displays an empty state when there are no duplicates to review.
 * Shows different messages based on the current filter.
 */

import { Link } from '@inertiajs/react';
import { Copy, ArrowLeft, CheckCircle } from 'lucide-react';
import type { DuplicateFilter } from '@/types/accounting';

export interface DuplicateEmptyStateProps {
  /** The current filter being applied */
  filter: DuplicateFilter;
}

/**
 * DuplicateEmptyState displays a friendly message when no duplicates exist.
 *
 * @example
 * <DuplicateEmptyState filter="unreviewed" />
 */
export function DuplicateEmptyState({ filter }: DuplicateEmptyStateProps) {
  const isUnreviewedFilter = filter === 'unreviewed';

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      data-testid="duplicate-empty-state"
    >
      <div className="px-6 py-16 text-center">
        {isUnreviewedFilter ? (
          <>
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-700 mb-2">
              No duplicates to review
            </h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
              All duplicate transactions have been reviewed. Great job keeping your transactions organized!
            </p>
          </>
        ) : (
          <>
            <Copy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-700 mb-2">
              No duplicates found
            </h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
              No transactions have been flagged as potential duplicates during import.
            </p>
          </>
        )}
        <Link
          href="/accounting/transactions"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <ArrowLeft size={16} />
          Back to Transactions
        </Link>
      </div>
    </div>
  );
}

export default DuplicateEmptyState;
